import { Box, Paper, Stack } from "@mui/material";
import { ExportDataButton } from "components/ExportDataButton";
import { type FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  CredentialImplementationsForStaffQuery,
  CredentialImplementationsForStaffQueryVariables,
} from "generated-graphql/graphql";
import { type FilterKey } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback, useState } from "react";

import { CREDENTIAL_IMPLEMENTATIONS_FOR_STAFF_QUERY } from "./api";
import { EpcraBulkActionsMenu } from "./BulkActions";
import { COLUMNS, type Row } from "./columns";
import { CredentialImplementationDrawer } from "./Drawer/CredentialImplementationDrawer";

const EXPORT_SORT = [{ field: "state", sort: "asc" }] as const;

const ADDITIONAL_FILTER_COLUMNS: FilterKey[] = [
  {
    key: "hasNotes",
    header: "Has Notes",
    filterKeyType: "boolean",
  },
];

const FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Facility",
    fields: ["tenant", "facility", "state", "county"],
  },
  {
    name: "Credential & Implementation",
    fields: [
      "credentialName",
      "credentialEmail",
      "credentialStatus",
      "implementationStatus",
      "assigneeEmail",
      "csmName",
    ],
  },
  {
    name: "Other Filters",
    fields: [
      "portalName",
      "credentialInEncamp",
      "lastVerifiedAt",
      "lockHolder",
      "seenInPortal",
      "stateId",
      "mostRecentComment",
      "notes",
      "hasNotes",
    ],
  },
];

export function EpcraCredentialManagement() {
  const {
    selectedId: credentialImplementationId,
    pushIdToUrl,
    setSelectedId,
    popIdFromUrl,
  } = useSelectedIdInUrl("credentialImplementationId");

  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<Row["id"][]>([]);
  const handleSelectedRowsChanged = useCallback((rows: Row[]) => {
    setSelectedRows(rows);
    setSelectedRowIds(rows.map((row) => row.id));
  }, []);
  const clearSelections = useCallback(() => {
    setSelectedRows([]);
    setSelectedRowIds([]);
  }, []);

  const getItems = useCallback(
    (data: CredentialImplementationsForStaffQuery) => data.credentialImplementationsForStaff.items,
    []
  );

  const getCount = useCallback(
    (data: CredentialImplementationsForStaffQuery) => data.credentialImplementationsForStaff.count,
    []
  );

  const commandButtons = useCallback(
    ({ search }: { search: string }) => [
      <Stack key="epcra-ci-commands" direction="row" spacing={1} alignItems="center">
        <EpcraBulkActionsMenu selectedRows={selectedRows} onClearSelection={clearSelections} />
        <ExportDataButton
          key="export"
          exportType="credentialImplementationsForStaff"
          fileName="epcra-credential-management"
          filter={{ search: search || undefined, sort: EXPORT_SORT }}
        />
      </Stack>,
    ],
    [clearSelections, selectedRows]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Box
        sx={{
          height: "100vh",
          pt: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <OmnisearchDataGrid<
          CredentialImplementationsForStaffQuery,
          CredentialImplementationsForStaffQueryVariables,
          Row
        >
          isURLDriven
          persistenceKey="epcra-credential-management-v2"
          columns={COLUMNS}
          commandButtons={commandButtons}
          dataQuery={CREDENTIAL_IMPLEMENTATIONS_FOR_STAFF_QUERY}
          initialSortModel={[{ field: "state", sort: "asc" }]}
          getItems={getItems}
          getCount={getCount}
          noDataMessage="No credential implementations found."
          initialPageSize={50}
          additionalFilterColumns={ADDITIONAL_FILTER_COLUMNS}
          wideLayout
          filterGroups={FILTER_GROUPS}
          debounceMs={800}
          fetchPolicy="network-only"
          checkboxSelection
          disableRowSelectionOnClick
          showLoadingOverlayOnRefetch={false}
          selectedIds={selectedRowIds}
          onSelectedRowsChanged={handleSelectedRowsChanged}
          gridSx={{
            height: "100%",
            minHeight: "400px",
            maxHeight: "calc(100vh - 280px)",
          }}
          onRowClick={({ row: { id } }) =>
            credentialImplementationId ? setSelectedId(id) : pushIdToUrl(id)
          }
        />
      </Box>
      <CredentialImplementationDrawer
        implementationId={credentialImplementationId}
        open={!!credentialImplementationId}
        onClose={popIdFromUrl}
      />
    </Paper>
  );
}
